import React, { useState } from 'react';
import {
  Box,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Paper,
  Divider,
  IconButton,
} from '@mui/material';
import { Visibility, Close } from '@mui/icons-material';

interface MarkdownEditorProps {
  value: string;
  onChange: (value: string) => void;
  onBlur?: () => void;
  placeholder?: string;
  label?: string;
  error?: boolean;
}

const MarkdownEditor: React.FC<MarkdownEditorProps> = ({
  value,
  onChange,
  onBlur,
  placeholder = "Start typing...",
  label,
  error = false,
}) => {
  const [previewOpen, setPreviewOpen] = useState(false);
  // const [previewMode, setPreviewMode] = useState<'preview' | 'edit'>('edit');

  // Simple markdown to HTML converter
  const markdownToHtml = (markdown: string): string => {
    let html = markdown
      // Headers
      .replace(/^### (.*$)/gim, '<h3>$1</h3>')
      .replace(/^## (.*$)/gim, '<h2>$1</h2>')
      .replace(/^# (.*$)/gim, '<h1>$1</h1>')
      // Bold
      .replace(/\*\*(.*)\*\*/gim, '<strong>$1</strong>')
      // Italic
      .replace(/\*(.*)\*/gim, '<em>$1</em>')
      // Links
      .replace(/\[([^\]]+)\]\(([^)]+)\)/gim, '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>')
      // Line breaks
      .replace(/\n/gim, '<br>')
      // Lists
      .replace(/^\* (.*$)/gim, '<li>$1</li>')
      .replace(/^- (.*$)/gim, '<li>$1</li>')
      .replace(/(<li>.*<\/li>)/s, '<ul>$1</ul>');

    // Wrap consecutive list items in ul tags
    html = html.replace(/(<li>.*?<\/li>(?:\s*<li>.*?<\/li>)*)/gs, '<ul>$1</ul>');
    
    return html;
  };

  const handlePreview = () => {
    setPreviewOpen(true);
  };

  const handleClosePreview = () => {
    setPreviewOpen(false);
  };

  // const togglePreviewMode = () => {
  //   setPreviewMode(previewMode === 'edit' ? 'preview' : 'edit');
  // };

  return (
    <Box sx={{ width: '100%', maxWidth: '100%' }}>
      <Box
        sx={{
          border: '1px solid #ddd',
          borderRadius: '4px',
          overflow: 'hidden',
          backgroundColor: '#fff',
          width: '100%',
          maxWidth: '100%',
        }}
      >
        {/* Editor Header */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '8px 12px',
            borderBottom: '1px solid #ddd',
            backgroundColor: '#f8f9fa',
          }}
        >
          <Typography variant="caption" sx={{ color: '#666', fontWeight: 500 }}>
            {label || 'Markdown Editor'}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              size="small"
              variant="outlined"
              startIcon={<Visibility />}
              onClick={handlePreview}
              sx={{
                fontSize: '12px',
                textTransform: 'none',
                minWidth: 'auto',
                px: 1.5,
                py: 0.5,
              }}
            >
              Preview
            </Button>
          </Box>
        </Box>

        {/* Editor Content */}
        <TextField
          multiline
          rows={8}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onBlur={onBlur}
          placeholder={placeholder}
          error={error}
          variant="standard"
          sx={{
            width: '100%',
            maxWidth: '100%',
            '& .MuiInput-root': {
              width: '100%',
              maxWidth: '100%',
              '&:before': {
                borderBottom: 'none',
              },
              '&:hover:not(.Mui-disabled):before': {
                borderBottom: 'none',
              },
              '&:after': {
                borderBottom: 'none',
              },
              '&.Mui-focused:after': {
                borderBottom: 'none',
              },
            },
            '& .MuiInputBase-input': {
              fontSize: '14px',
              lineHeight: 1.5,
              fontFamily: 'monospace',
              padding: '12px',
              resize: 'vertical',
              border: 'none',
              outline: 'none',
              backgroundColor: 'transparent',
              width: '100%',
              maxWidth: '100%',
              boxSizing: 'border-box',
            },
          }}
        />
      </Box>

      {/* Preview Dialog */}
      <Dialog
        open={previewOpen}
        onClose={handleClosePreview}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            minHeight: '70vh',
          },
        }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">Markdown Preview</Typography>
          <IconButton onClick={handleClosePreview} size="small">
            <Close />
          </IconButton>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ p: 3 }}>
          <Paper
            elevation={0}
            sx={{
              p: 3,
              border: '1px solid #e0e0e0',
              borderRadius: 1,
              minHeight: '400px',
              backgroundColor: '#fafafa',
            }}
          >
            {value ? (
              <Box
                dangerouslySetInnerHTML={{
                  __html: markdownToHtml(value),
                }}
                sx={{
                  '& h1': {
                    fontSize: '2rem',
                    fontWeight: 'bold',
                    marginBottom: '1rem',
                    color: '#333',
                  },
                  '& h2': {
                    fontSize: '1.5rem',
                    fontWeight: 'bold',
                    marginBottom: '0.8rem',
                    color: '#333',
                  },
                  '& h3': {
                    fontSize: '1.25rem',
                    fontWeight: 'bold',
                    marginBottom: '0.6rem',
                    color: '#333',
                  },
                  '& p': {
                    marginBottom: '1rem',
                    lineHeight: 1.6,
                  },
                  '& ul': {
                    marginBottom: '1rem',
                    paddingLeft: '1.5rem',
                  },
                  '& li': {
                    marginBottom: '0.5rem',
                    listStyleType: 'disc',
                  },
                  '& strong': {
                    fontWeight: 'bold',
                  },
                  '& em': {
                    fontStyle: 'italic',
                  },
                  '& a': {
                    color: '#1976d2',
                    textDecoration: 'underline',
                    '&:hover': {
                      color: '#1565c0',
                    },
                  },
                }}
              />
            ) : (
              <Typography variant="body2" color="text.secondary">
                No content to preview. Start typing to see the preview.
              </Typography>
            )}
          </Paper>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleClosePreview} variant="contained">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MarkdownEditor;
